import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Account/redux/account.selectors'
import { selectCreditCardIdFromRoute } from 'src/app/AccountTransactions/redux/accountTransactions.selectors'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/AccountVirtualCards/redux/accountVirtualCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { budgetVirtualCardsUrl } from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const AccountVirtualCards: React.FC = () => {
  const creditCardId = useSelector(selectCreditCardIdFromRoute)
  const { data: creditCard } = useQueryCreditCard({
    creditCardId: creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.id,
    enabled: !!creditCard?.id,
    retry: false,
  })

  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)
  const queryParams = useSelector(selectQueryParams)
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)

  const currentUser = useSelector(selectUserListModelFromState)

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    dispatch(
      postReportVirtualCardsDownload({
        ...filters,
        creditCardId: creditCard?.id,
      }),
    )
  }

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        statuses: [
          VirtualCardStatus.Active,
          VirtualCardStatus.Cancelled,
          VirtualCardStatus.Consumed,
          VirtualCardStatus.Closed,
          VirtualCardStatus.Expired,
        ],
        issued: true,
        count: 10,
        page: page,
        creditCardId: creditCard?.id,
        ...filtersBody,
      }),
    )
  }

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  // redirect to budget virtual cards if budget
  useEffect(() => {
    if (creditCard?.isBudget) {
      dispatch(connectedReplace(budgetVirtualCardsUrl(creditCard.id)))
    }
  }, [creditCard])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('account virtual cards filter applied', {
        card_user:
          selectedFilters.card_user && selectedFilters.card_user.length > 0,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        deactivating_in: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return (
    <div
      css={css`
        height: 100%;
      `}
      ref={ref}
      id={'ref'}
    >
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          onDownloadClick={
            canUpdateCreditCard
              ? dispatchPostReportVirtualCardsDownload
              : undefined
          }
          virtualCards={filteredVirtualCards}
          defaultTitle={
            <ObjectBoxTitle
              title={<Trns source={'Virtual Cards Created'} />}
              static={{
                label: <Trns source={'Active'} />,
                count: activeVcnCount || 0,
              }}
            />
          }
          emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
          infiniteScrollProps={infiniteScrollProps}
          totalCount={vcnCount || 0}
          initialFilterValues={{
            sortBy: 'activeClosedUpdatedAt',
            sortDescending: false,
            recipients: useQueryParamArrayHandler(queryParams.recipients),
            keyword: queryParams.search || '',
            statuses: useQueryParamArrayHandler(queryParams.statuses),
          }}
          readOnly={
            creditCard?.ownerId !== currentUser?.id || creditCard?.isClosed
          }
          usersFullPaginatedData={usersFullPaginatedData}
          setSelectedFilters={setSelectedFilters}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </div>
  )
}

export default AccountVirtualCards
